// Client-side checks for a creator post before any request is made.

import { VIDEO_CONTENT_TYPES } from './postMedia';

export const ALLOWED_POST_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
/** The same list the read path already mirrors from the backend's posts.util.ts. */
export const ALLOWED_POST_VIDEO_TYPES = VIDEO_CONTENT_TYPES;
export const ALLOWED_POST_MEDIA_TYPES: string[] = [
  ...ALLOWED_POST_IMAGE_TYPES,
  ...ALLOWED_POST_VIDEO_TYPES,
];

// Both caps mirror the backend's `MAX_IMAGE_BYTES` / `MAX_VIDEO_BYTES`. The API
// cannot enforce them — a v4 signed PUT binds a content type, not a length —
// so this is the one place they are checked.
export const MAX_POST_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_POST_VIDEO_BYTES = 200 * 1024 * 1024;
export const MAX_POST_CAPTION_LENGTH = 2000;

export type ValidationResult = { ok: true } | { ok: false; reason: string };

export type PostMediaKind = 'image' | 'video';

/** Structural shape of a picked file, so a real `File` fits without a DOM in tests. */
export interface PostMediaCandidate {
  type: string;
  size: number;
}

/**
 * What the composer would upload this as, or null for a type it may not upload
 * at all. Never guesses, unlike `readMediaKind` in postMedia.ts, which defaults
 * an unknown type to 'image' because it is reading a post that already exists.
 */
export function postMediaKind(contentType: string): PostMediaKind | null {
  if ((ALLOWED_POST_IMAGE_TYPES as readonly string[]).includes(contentType)) {
    return 'image';
  }
  if (ALLOWED_POST_VIDEO_TYPES.includes(contentType)) {
    return 'video';
  }
  return null;
}

export function maxBytesForPostMedia(kind: PostMediaKind): number {
  return kind === 'video' ? MAX_POST_VIDEO_BYTES : MAX_POST_IMAGE_BYTES;
}

export function validatePostMedia(file: PostMediaCandidate | null): ValidationResult {
  if (!file) {
    return { ok: false, reason: 'Choose a photo or video to post.' };
  }

  const kind = postMediaKind(file.type);
  if (!kind) {
    return { ok: false, reason: 'Photos (JPEG, PNG, WebP) or videos (MP4, WebM) only.' };
  }

  if (file.size === 0) {
    return { ok: false, reason: 'That file looks empty.' };
  }

  if (file.size > maxBytesForPostMedia(kind)) {
    return kind === 'video'
      ? { ok: false, reason: 'That video is larger than 200 MB. Choose a smaller one.' }
      : { ok: false, reason: 'That image is larger than 10 MB. Choose a smaller one.' };
  }

  return { ok: true };
}

export function validatePostCaption(caption: string): ValidationResult {
  if (caption.length > MAX_POST_CAPTION_LENGTH) {
    return {
      ok: false,
      reason: `Captions can be at most ${MAX_POST_CAPTION_LENGTH} characters (you have ${caption.length}).`,
    };
  }
  return { ok: true };
}

export function validateNewPost(
  file: PostMediaCandidate | null,
  caption: string,
): ValidationResult {
  const mediaResult = validatePostMedia(file);
  if (!mediaResult.ok) {
    return mediaResult;
  }
  return validatePostCaption(caption);
}

/** A picked file's size as a creator reads it, or '' when there is no number. */
export function formatMediaSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return '';
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  const mb = bytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : Number(mb.toFixed(1))} MB`;
}

/**
 * Strips a browser file name down to what the upload-url endpoint accepts
 * (`/^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/`): no path segments, no spaces, no
 * leading dot. Falls back to a generic name when nothing usable is left.
 */
export function safePostFilename(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? '';
  const cleaned = base.replace(/[^A-Za-z0-9._-]/g, '-').replace(/^[^A-Za-z0-9]+/, '');
  return (cleaned || 'image').slice(0, 100);
}
